import React from "react";

import styled from "styled-components";

import { LgStatusIcon } from "manager/components/app-summary/AppListV2/StatusIcon";
import {
  NO_VERDICT_LABEL,
  categorySortKey,
  clientNameFor,
  displayName,
  effectiveTone,
  formatCategory,
  formatItemValue,
  isAnnotationOnlyItem,
  isScoreCriterion,
  lumenLabel,
} from "manager/components/vero1/Vero1ReviewPanel/formatItemValue";
import { categoryAnchorId } from "manager/components/vero1/openDealReview";
import {
  hasResultMoved,
  isLeasingTouched,
} from "manager/components/vero1/Vero1ReviewPanel/itemState";
import { orderRowsInGroup } from "manager/components/vero1/Vero1ReviewPanel/orderRows";
import { UnassessedMarker } from "manager/components/vero1/Vero1ReviewPanel/rowStyled";
import type {
  Vero1Checklist,
  Vero1ChecklistItem,
  Vero1Severity,
} from "shared/interfaces/api/vero1";
import {
  RECOMMENDATION_LABELS,
  resolveVero1RecommendationName,
  Vero1ItemType,
  Vero1Recommendation,
  Vero1RowKind,
} from "shared/interfaces/api/vero1";

import { formatRunStamp } from "../vero1Dates";

import {
  DIVIDER,
  GroupHeading,
  GroupHeadingSub,
  HeaderMeta,
  HeaderPad,
  HeaderRow,
  INK,
  MetaLine,
  HoldDot,
  HoldStrip,
  HoldText,
  MUTED,
  RationaleBlock,
  RationaleLabel,
  RationaleText,
  RowApplicant,
  RowFindings,
  RowName,
  SectionBody,
  SectionCard,
  SectionTitle,
  SEVERITY_CHIP_TONE,
  TitleBlock,
  TitleLine,
  VerdictChip,
  VerdictRule,
  VIOLET,
} from "./styled";

// leasing view of the latest submission: read-only facts, transitions in the client's names, findings only

const Row = styled.div<{ $last?: boolean }>`
  border-bottom: ${({ $last }) => ($last ? "none" : `1px solid ${DIVIDER}`)};
  padding: 11px 0;
`;

const RowLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Pip = styled.span<{ $touched?: boolean }>`
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $touched }) => ($touched ? VIOLET : "transparent")};
`;

const ActionLine = styled.p`
  font: 500 11.5px/1.5 "Work Sans", sans-serif;
  color: #b35500;
  margin: 3px 0 0 45px;
`;

const FactValue = styled.span`
  font: 500 13px ui-monospace, Menlo, monospace;
  color: ${INK};
  white-space: nowrap;
`;

const TransitionPill = styled.span`
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 2px;
  background: #f4f1fc;
  border: 1px solid #dcd3f3;
  font: 500 10.5px "Work Sans", sans-serif;
  color: #5b3fa8;
  white-space: nowrap;
`;

export { SEVERITY_CHIP_TONE };

/** Keyed by fact, not id: the id changes with every version. */
export const rowKey = (item: Vero1ChecklistItem) =>
  item.itemType === Vero1ItemType.Custom
    ? `custom:${item.label}:${item.application ?? "deal"}`
    : `${item.criterionKey}:${item.application ?? "deal"}`;

const income = (item: Vero1ChecklistItem) =>
  item.criterionKey === "total-verified-income" ? " / yr" : "";

const factValue = (item: Vero1ChecklistItem): string | null => {
  if (item.rowKind === Vero1RowKind.Sentilink || isAnnotationOnlyItem(item)) {
    return lumenLabel(item);
  }
  if (item.originalValue == null && !isScoreCriterion(item.criterionKey)) {
    return null;
  }
  const formatted = formatItemValue(item.originalValue, item.criterionKey);
  return formatted === "—" ? null : formatted + income(item);
};

export const LeasingRow: React.FC<{
  item: Vero1ChecklistItem;
  last?: boolean;
}> = ({ item, last }) => {
  const tone = effectiveTone(item);
  // a same-severity rename moves the result too
  const moved = item.resultMoved ?? hasResultMoved(item);
  const standingTone = !moved && tone !== null && tone !== "pass" ? tone : null;
  const standingName = standingTone ? clientNameFor(standingTone, item) : null;
  // the agent's stored pick is the result; the configured name is the fallback
  const pickedName =
    displayName(item.overrideOutcomeName) ||
    clientNameFor(item.overrideSeverity, item);
  const value = factValue(item);
  return (
    <Row data-testid="leasing-review-row" $last={last}>
      <RowLine>
        <Pip $touched={isLeasingTouched(item)} aria-hidden />
        {tone === null ? (
          <UnassessedMarker data-testid="leasing-unassessed-marker" aria-hidden />
        ) : (
          <LgStatusIcon
            status={tone}
            size={22}
            testId={`leasing-check-state-${tone}`}
          />
        )}
        <RowName>
          {item.label}
          {item.applicantName && (
            <RowApplicant>{item.applicantName}</RowApplicant>
          )}
        </RowName>
        {moved && (
          <TransitionPill data-testid="leasing-transition">
            {clientNameFor(item.originalSeverity, item) ?? NO_VERDICT_LABEL} →{" "}
            {pickedName}
          </TransitionPill>
        )}
        {standingTone && standingName && (
          <VerdictChip
            $tone={SEVERITY_CHIP_TONE[standingTone]}
            style={{ height: 19, fontSize: 10.5 }}
            data-testid="leasing-verdict-name"
          >
            {standingName}
          </VerdictChip>
        )}
        {value !== null && <FactValue>{value}</FactValue>}
      </RowLine>
      {item.overrideExplanation && (
        <RowFindings $indent={45}>{item.overrideExplanation}</RowFindings>
      )}
      {item.actionForLeasing && (
        <ActionLine>Action needed: {item.actionForLeasing}</ActionLine>
      )}
    </Row>
  );
};

export const groupItems = (items: Vero1ChecklistItem[]) => {
  const standard = items.filter((i) => i.itemType !== Vero1ItemType.Custom);
  const custom = orderRowsInGroup(
    items.filter((i) => i.itemType === Vero1ItemType.Custom),
    { by: "effective" }
  );
  const categories = Array.from(new Set(standard.map((i) => i.category))).sort(
    (a, b) => categorySortKey(a) - categorySortKey(b)
  );
  return { categories, standard, custom };
};

export const LeasingReviewSection: React.FC<{
  checklist: Vero1Checklist;
  holdNote?: string | null;
}> = ({ checklist, holdNote }) => {
  const { categories, standard, custom } = groupItems(checklist.items || []);
  // leasing never sees the AGREE token — the adopted outcome is the record
  const effective =
    checklist.finalRecommendation === Vero1Recommendation.Agree
      ? checklist.originalOutcome ?? null
      : checklist.finalRecommendation;
  // AC 4: leasing reads the client's configured name, never Vero's enum
  const recommendation = effective
    ? resolveVero1RecommendationName(checklist) ??
      RECOMMENDATION_LABELS[effective] ??
      effective
    : checklist.finalRecommendation
    ? "Reviewed"
    : null;
  const submitted = checklist.submittedAt
    ? formatRunStamp(checklist.submittedAt)
    : null;
  return (
    <SectionCard data-testid="deal-review-leasing">
      <HeaderPad>
        <HeaderRow>
          <TitleBlock>
            <TitleLine>
              <SectionTitle>VERO1 review</SectionTitle>
              {submitted && <HeaderMeta>Submitted {submitted}</HeaderMeta>}
            </TitleLine>
            <MetaLine>Reviewed by the VERO1 team</MetaLine>
          </TitleBlock>
          {recommendation && (
            <VerdictChip $tone={effective ?? undefined}>
              {recommendation}
            </VerdictChip>
          )}
        </HeaderRow>
        {/* the chip stays — it is the record — but the rule goes quiet while
            the review holds, so the header cannot read as a live verdict */}
        <VerdictRule $tone={holdNote ? undefined : effective ?? undefined} />
      </HeaderPad>
      {holdNote && (
        <HoldStrip data-testid="deal-review-hold">
          <HoldDot aria-hidden />
          <HoldText>{holdNote}</HoldText>
        </HoldStrip>
      )}
      {checklist.decisionRationale && (
        <RationaleBlock>
          <RationaleLabel>Why this recommendation</RationaleLabel>
          <RationaleText>{checklist.decisionRationale}</RationaleText>
        </RationaleBlock>
      )}
      {checklist.clientFindings && (
        <RationaleBlock>
          <RationaleLabel>Findings</RationaleLabel>
          <RationaleText>{checklist.clientFindings}</RationaleText>
        </RationaleBlock>
      )}
      <SectionBody>
        {categories.map((category, index) => {
          const rows = orderRowsInGroup(
            standard.filter((i) => i.category === category),
            { by: "effective" }
          );
          return (
            <React.Fragment key={category}>
              <GroupHeading
                id={categoryAnchorId(category)}
                $first={index === 0}
              >
                {formatCategory(category)}
              </GroupHeading>
              {rows.map((row, rowIndex) => (
                <LeasingRow
                  key={rowKey(row)}
                  item={row}
                  last={
                    custom.length === 0 &&
                    index === categories.length - 1 &&
                    rowIndex === rows.length - 1
                  }
                />
              ))}
            </React.Fragment>
          );
        })}
        {custom.length > 0 && (
          <>
            <GroupHeading $first={categories.length === 0}>
              Custom items <GroupHeadingSub>— added by VERO1</GroupHeadingSub>
            </GroupHeading>
            {custom.map((row, rowIndex) => (
              <LeasingRow
                key={rowKey(row)}
                item={row}
                last={rowIndex === custom.length - 1}
              />
            ))}
          </>
        )}
      </SectionBody>
    </SectionCard>
  );
};
